import dayjs from "dayjs";
import { TFunction } from "i18next";
import {
  DATE_TIME_INDICATOR,
  DUE_THRESHOLD,
  END_OF_DAY_TIME,
  HOURS_IN_DAY,
  MILLISECONDS_IN_DAY,
  MILLISECONDS_IN_HOUR,
  MILLISECONDS_IN_MINUTE,
  MILLISECONDS_IN_SECOND,
  MINUTES_IN_HOUR,
} from "utils/constants/time.constants";
import { formatDate } from "utils/helpers";
import { TISODateFormat } from "utils/types";

export const isDue = (hours: number) => hours <= DUE_THRESHOLD && hours >= 0;
export const isUpcoming = (hours: number) => hours > DUE_THRESHOLD;
export const isOverdue = (hours: number) => hours < 0;

const getOffset = (timeZone = "GMT", date = new Date()) => {
  const utcDate = new Date(date.toLocaleString("en-US", { timeZone: "GMT" }));
  const tzDate = new Date(date.toLocaleString("en-US", { timeZone }));

  return (tzDate.getTime() - utcDate.getTime()) / MILLISECONDS_IN_HOUR;
};

const getThisMachineOffset = () =>
  new Date().getTimezoneOffset() / MINUTES_IN_HOUR;

const applyTimezone = (date: Date, timezone = "GMT") => {
  const thisMachineOffset = getThisMachineOffset();
  const userOffset = timezone ? getOffset(timezone) : getThisMachineOffset();

  return new Date(
    date.setHours(date.getHours() + thisMachineOffset + userOffset),
  );
};

/**
 * Returns the difference in hours between the current time and just before midnight on the due date
 * in UTC. Since dueDateString is a simple date string and not timezone aware, the caller of this
 * function must assume that UTC time will be used. This is not affected by the time zone in the
 * user's preferences, since the current time is the current time no matter what the time zone is.
 * Dayjs accounts for the fact that the browser will return the current time in its system time zone
 * when it does the diff calculation with the UTC time.
 */
export const getWorkingTimeDifference = (dueDateString: TISODateFormat) => {
  const now = dayjs();

  const due = dayjs.utc(
    `${dueDateString}${DATE_TIME_INDICATOR}${END_OF_DAY_TIME}`,
  );

  return Math.round(due.diff(now, "hours", true));
};

export const getTimeString = (dueTime: number, currentLanguage: string) => {
  const rtf = new Intl.RelativeTimeFormat(currentLanguage, {
    numeric: "always",
    style: "long",
  });

  const days = Math.trunc(dueTime / HOURS_IN_DAY);
  const hours = dueTime % HOURS_IN_DAY;

  if (Math.abs(days) > 0) {
    return rtf.format(Math.round(days), "day");
  } else if (Math.abs(hours) >= 0) {
    return rtf.format(Math.round(hours), "hour");
  }
};

export const getDueTimeString = (
  dueTime: number,
  dueDate: TISODateFormat,
  t: TFunction,
  currentLanguage: string,
) => {
  if (isUpcoming(dueTime)) {
    return `${t("time.due")}: ${formatDate(dueDate)}`;
  } else if (isDue(dueTime)) {
    return `${t("time.due")} ${getTimeString(dueTime, currentLanguage)}`;
  } else {
    return `${t("time.overdue")} ${getTimeString(dueTime, currentLanguage)}`;
  }
};

export const getDatesDelta = (dateA: Date, dateB: Date = new Date()) => {
  const mSecsDiff = dateA.getTime() - dateB.getTime();

  const secsDiff = mSecsDiff / MILLISECONDS_IN_SECOND;
  const minutesDiff = mSecsDiff / MILLISECONDS_IN_MINUTE;
  const hoursDiff = mSecsDiff / MILLISECONDS_IN_HOUR;
  const daysDiff = mSecsDiff / MILLISECONDS_IN_DAY;

  let delta: number;
  let unit: Intl.RelativeTimeFormatUnit;

  if (Math.abs(daysDiff) >= 1) {
    delta = daysDiff;
    unit = "day";
  } else if (Math.abs(hoursDiff) >= 1) {
    delta = hoursDiff;
    unit = "hour";
  } else if (Math.abs(minutesDiff) >= 1) {
    delta = minutesDiff;
    unit = "minute";
  } else {
    delta = secsDiff;
    unit = "second";
  }

  return { delta, unit };
};

/**
 * Create a human-readable relative date/time string.
 * @param relativeTo past / future date to use for relative time
 * @param currentLanguage taken from i18n
 * @param now current date (optional)
 * @returns relative date/time string (e.g. "5 days ago", "1 hour ago", "20 minutes ago", "10 seconds ago")
 */
export const getRelativeTimeString = (
  relativeTo: Date | undefined,
  currentLanguage: string,
  now: Date = new Date(),
) => {
  if (!relativeTo) {
    return "";
  }

  const { delta, unit } = getDatesDelta(relativeTo, now);

  const rtf = new Intl.RelativeTimeFormat(currentLanguage, {
    numeric: "always",
    style: "long",
  });

  return rtf.format(Math.round(delta), unit);
};

/**
 * Create relative date/time array.
 * @param relativeTo past / future date to use for relative time
 * @param currentLanguage taken from i18n
 * @param now current date (optional)
 * @returns relative date/time array for custom locale-aware formatting
 * (e.g. [{ type: "literal", value: "in " }, { type: "integer", value: "100", unit: "day" }, { type: "literal", value: " days" }])
 */
export const getRelativeTimeArray = (
  relativeTo: Date | undefined,
  currentLanguage: string,
  now: Date = new Date(),
) => {
  if (!relativeTo) {
    return [];
  }

  const { delta, unit } = getDatesDelta(relativeTo, now);

  const rtf = new Intl.RelativeTimeFormat(currentLanguage, {
    numeric: "always",
    style: "short",
  });

  return rtf.formatToParts(Math.round(delta), unit);
};

export const parseDateWithTimezone = (
  dueDateString: TISODateFormat | string,
  timezone = "GMT",
) => {
  const [dateString, timeString] = dueDateString?.split(DATE_TIME_INDICATOR);

  return applyTimezone(
    new Date(
      `${dateString}${DATE_TIME_INDICATOR}${timeString ?? END_OF_DAY_TIME}`,
    ),
    timezone,
  );
};
